import { useState, useRef } from "react";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface MediaUploaderProps {
  onMediaUpload: (preview: string | null, type: "image" | "video" | null) => void;
}

export const MediaUploader = ({ onMediaUpload }: MediaUploaderProps) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const isValidFileType = (file: File) => {
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    return [...validImageTypes, ...validVideoTypes].includes(file.type);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    // Validate file types
    const invalidFiles = files.filter(file => !isValidFileType(file));
    if (invalidFiles.length > 0) {
      toast({
        title: "Invalid file type",
        description: "Please upload only images (JPG, PNG, GIF, WebP) or videos (MP4, QuickTime, WebM).",
        variant: "destructive",
      });
      return;
    }

    // Check total size (50MB limit)
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > 52428800) {
      toast({
        title: "Files too large",
        description: "Total file size must be under 50MB.",
        variant: "destructive",
      });
      return;
    }

    setMediaFiles(files);

    try {
      setUploadProgress(20);
      
      // Upload each file
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
        
        const { data, error } = await supabase.storage
          .from('post-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) throw error;
        
        setUploadProgress(prev => prev + (80 / files.length));

        const { data: { publicUrl } } = supabase.storage
          .from('post-images')
          .getPublicUrl(fileName);

        onMediaUpload(publicUrl, file.type.startsWith('video/') ? "video" : "image");
      }

      setUploadProgress(100);
      
      toast({
        title: "Upload complete! 🎉",
        description: `${files.length} file${files.length > 1 ? 's' : ''} uploaded successfully.`,
      });

      // Reset progress after a short delay
      setTimeout(() => setUploadProgress(0), 1000);
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadProgress(0);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload files. Please try again.",
        variant: "destructive",
      });
    }
  };

  const removeFile = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
        multiple
        className="hidden"
      />
      <Button
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        className="flex-1 border-2 border-purple-200 hover:bg-purple-50 rounded-xl"
      >
        <Upload className="w-4 h-4 mr-2 text-purple-500" />
        Upload Media
      </Button>

      {mediaFiles.length > 0 && (
        <div className="space-y-2">
          {mediaFiles.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
              <span className="text-sm text-purple-700 truncate">{file.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFile(index)}
                className="text-purple-500 hover:text-purple-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {uploadProgress > 0 && (
        <div className="space-y-2">
          <Progress value={uploadProgress} className="w-full h-2 bg-purple-100" />
          <p className="text-sm text-center text-purple-600">
            Uploading... {uploadProgress}%
          </p>
        </div>
      )}
    </div>
  );
};